import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import { useState } from "react";
import { Button, Card, CardBody, CardHeader, Col, Form, Row, Input, Label, FormGroup, Accordion, AccordionBody, AccordionHeader, AccordionItem } from "reactstrap";
import { addAIAgent, setInitialValues } from '../../utils/middleware/AIAgents';
import { REACT_APP_VOICE_AGENT_URL } from "../../../../constants/Configs"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
import { editProfileValidationSchema } from '../../../../constants/Schemas';
import "./style.css";
import { Info, Plus, GripVertical, ToggleLeft, ToggleRight, Trash2 } from 'lucide-react';

// 🔑 Replace with actual token handling
const API_TOKEN = "YOUR_TOKEN";
const BASE_URL = "https://backend.omnidim.io/api/v1/knowledge_base";

export default function AIProfile() {
  // Store
  const { restaurant } = useSelector(state => state.restaurantAdmin);

  const [editable, setEditable] = useState(false);
  const [triggerReplies, setTriggerReplies] = useState([{ title: "", body: "", is_enabled: true }]);

  const [sections, setSections] = useState([
    {
      id: 1,
      title: '',
      content: "",
      isExpanded: true,
      isEnabled: true,
    }
  ]);
  const toggleSection = (id) => {
    setSections(
      sections.map((section) =>
        section.id === id ? { ...section, isExpanded: !section.isExpanded } : section
      )
    );
  };

  const toggleSectionEnabled = (id) => {
    setSections(
      sections.map((section) =>
        section.id === id ? { ...section, isEnabled: !section.isEnabled } : section
      )
    );
  };

  const addSection = () => {
    const newSection = {
      id: sections.length + 1,
      title: '',
      content: '',
      isExpanded: true,
      isEnabled: true,
    };
    setSections([...sections, newSection]);
  };

  const deleteSection = (id) => {
    setSections(sections.filter((section) => section.id !== id));
  };

  const updateSectionTitle = (id, newTitle) => {
    setSections(
      sections.map((section) =>
        section.id === id ? { ...section, title: newTitle } : section
      )
    );
  };

  const updateSectionContent = (id, newContent) => {
    setSections(
      sections.map((section) =>
        section.id === id ? { ...section, content: newContent } : section
      )
    );
  };

  // File upload state
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState("");

  // ========= File Upload Methods =========
  const checkEligibility = async (file) => {
    try {
      const payload = {
        file_size: file.size,
        file_type: file.type.split("/")[1], // e.g. pdf, docx
      };
      const res = await fetch(`${BASE_URL}/can_upload`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${API_TOKEN}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      return await res.json();
    } catch (err) {
      console.error("Eligibility Check Error:", err);
      return { success: false, message: "Error checking file eligibility" };
    }
  };

  const uploadFile = async (file) => {
    try {
      setUploading(true);
      setUploadMessage("Checking file eligibility...");

      const eligibility = await checkEligibility(file);

      if (!eligibility.success) {
        setUploading(false);
        setUploadMessage(`Upload not allowed: ${eligibility.message || "Invalid file"}`);
        return;
      }

      // Convert file to Base64
      const toBase64 = (f) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(f);
        reader.onload = () => resolve(reader.result.split(",")[1]); // remove prefix
        reader.onerror = (error) => reject(error);
      });

      const base64File = await toBase64(file);

      setUploadMessage("Uploading file to knowledge base...");

      const payload = {
        file: base64File,
        filename: file.name,
      };

      const res = await fetch(`${BASE_URL}/create`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${API_TOKEN}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await res.json();

      if (result.success) {
        setUploadMessage(`✅ File uploaded successfully: ${file.name}`);
      } else {
        setUploadMessage(`❌ Upload failed: ${result.message || "Unknown error"}`);
      }

    } catch (err) {
      console.error("Upload Error:", err);
      setUploadMessage("❌ Upload failed due to error.");
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) uploadFile(file);
  };

  // ========= Formik =========
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: setInitialValues(restaurant),
    validationSchema: editProfileValidationSchema,
    onSubmit: async () => {
      handleSubmit();
    }
  });

  // ========= Handle Submit =========
  const handleSubmit = () => {
    try {
      const restaurantAdmin = JSON.parse(localStorage.getItem('userData'));
      const payload = {
        restaurantId: restaurantAdmin.restaurantId,
        name: restaurantAdmin.name,
        welcome_message: formik.values.welcomeMessage,
        context_breakdown: sections,
        transcriber: "default",
        model: "gpt-4",
        voice: "en-US",
        web_search: formik.values.webSearch,
        post_call_actions: [],
        webhook: `${REACT_APP_VOICE_AGENT_URL}/webhook`,
        webhookUrl: `${REACT_APP_VOICE_AGENT_URL}/webhook`,
        filler: "uh"
      };
      console.log("API Called", payload);
      addAIAgent(payload);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div>
      <Card className="section-primary shadow">
        <CardHeader className="space-between-div table-header-div">
          <h3>AI Configurations</h3>
          <div>
            {!editable && (
              <Button color="primary" onClick={() => setEditable(true)} className="me-2">
                Edit
              </Button>
            )}
            {editable && (
              <>
                <Button color="success" className="me-2" onClick={handleSubmit} type="button">
                  Save
                </Button>
                <Button
                  color="secondary"
                  className="me-2"
                  onClick={() => {
                    setEditable(false);
                    formik.resetForm();
                  }}
                  type="button"
                >
                  Cancel
                </Button>
              </>
            )}
            <Button
              color="warning"
              type="button"
              onClick={() => {
                formik.resetForm();
                setTriggerReplies([{ title: "", message: "", is_enabled: true }]);
              }}
            >
              Default
            </Button>
          </div>
        </CardHeader>
        <CardBody className='user-profile-block'>
          <Form>
            <h4>Business-Specific Phrases</h4>
            <Row>
              <Col md={12}>
                <Label className="inputfield">Welcome Message</Label>
                <Input
                  type="text"
                  name="welcomeMessage"
                  className="custom-form-control mb-3"
                  value={formik.values.welcomeMessage}
                  onChange={formik.handleChange}
                  disabled={!editable}
                />
              </Col>
            </Row>

            {/* Conversational Flow Section */}
            <Card className="ai-card">
              <CardBody>
                <div className="ai-section-header d-flex justify-content-between align-items-center mb-4">
                  <div className="ai-section-title-group d-flex align-items-center">
                    <h5 className="ai-section-title me-2">
                      Conversational Flow (Assistant&apos;s Instructions)
                    </h5>
                    <Info size={16} className="ai-icon" />
                  </div>
                  <Button outline color="danger" size="sm" onClick={addSection}>
                    <Plus size={16} className="me-1" />
                    Add Section
                  </Button>
                </div>

                {/* Flow Sections */}
                <div className="ai-flow-sections">
                  {sections.map((section, index) => (
                    <div key={section.id} className="ai-flow-section mb-3">
                      <div className="ai-section-item d-flex justify-content-between align-items-center">
                        {/* <div className="ai-section-controls d-flex align-items-center">
                          <Button
                            color="link"
                            className="ai-expand-btn me-2"
                            onClick={() => toggleSection(section.id)}
                          >
                            {section.isExpanded ? '−' : '+'}
                          </Button>
                          <GripVertical size={16} className="ai-drag-handle me-2" />
                          <span className="ai-section-number me-1">{index + 1}.</span>
                          <Input
                            type="text"
                            value={section.title}
                            onChange={(e) =>
                              updateSectionTitle(section.id, e.target.value)
                            }
                            className="ai-section-title-input"
                          />
                        </div> */}
                        <div className="ai-section-controls d-flex align-items-center flex-grow-1">
                          <button
                            type="button"
                            className="ai-expand-btn me-2"
                            onClick={() => toggleSection(section.id)}
                          >
                            {section.isExpanded ? '−' : '+'}
                          </button>

                          <GripVertical size={16} className="ai-drag-handle me-2" />
                          <span className="ai-section-number me-2">{index + 1}.</span>

                          {/* Responsive title input */}
                          <Input
                            type="text"
                            value={section.title}
                            disabled={!editable}
                            onChange={(e) => updateSectionTitle(section.id, e.target.value)}
                            className="ai-section-title-input flex-grow-1"
                          />
                        </div>

                        <div className="ai-section-actions d-flex align-items-center">
                          <span className="ai-on-label me-2">ON</span>
                          <Button
                            color="link"
                            className="ai-toggle-btn me-2"
                            onClick={() => toggleSectionEnabled(section.id)}
                          >
                            {section.isEnabled ? (
                              <ToggleRight size={24} className="ai-toggle-on" />
                            ) : (
                              <ToggleLeft size={24} className="ai-toggle-off" />
                            )}
                          </Button>
                          <Button
                            color="link"
                            className="ai-delete-btn"
                            onClick={() => deleteSection(section.id)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                      {section.isExpanded && (
                        <div className="ai-section-content mt-2">
                          <Input
                            type="textarea"
                            rows="4"
                            value={section.content}
                            disabled={!editable}
                            onChange={(e) =>
                              updateSectionContent(section.id, e.target.value)
                            }
                            className="ai-section-textarea"
                            placeholder="Enter section instructions..."
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>

            {/* 📂 File Upload Section */}
            {/* <h4>Knowledge Base File Upload</h4>
            <Row className="mb-3">
              <Col md={12}>
                <FormGroup>
                  <Label for="exampleFile">
                    File
                  </Label>
                  <Input
                    id="exampleFile"
                    accept=".pdf,.doc,.docx,.txt"
                    onChange={handleFileChange}
                    disabled={uploading}
                    name="file"
                    type="file"
                  />
                  <FormText>
                    {uploadMessage && <p className="mt-2">{uploadMessage || "This is some placeholder block - level help text for the above input.It‘s a bit lighter and easily wraps to a new line."}</p>}

                  </FormText>
                </FormGroup>
              </Col>
            </Row> */}

            <h4>Advance Settings</h4>
            <Row>
              <Col md={12}>
                <FormGroup switch>
                  <Input
                    type="switch"
                    disabled={!editable}
                    role="switch"
                    name="webSearch"
                    className="custom-form-control mb-3"
                    value={formik.values.webSearch}
                    onChange={formik.handleChange}
                  />
                  <Label check>Web Search</Label>
                </FormGroup>
              </Col>
            </Row>
          </Form>
        </CardBody>
      </Card>
    </div>
  );
}
